import { ChangeEvent, useContext, useState } from 'react'
import {
	FilterField,
	FiltersPanel,
	FiltersToggle,
	SearchInput,
	Select,
	UserSelect
} from 'shared/components'
import { useTranslation } from 'libs/i18n'
import { useUserRoles, useFetchInfinite } from 'shared/hooks'
import { useFilters } from '../../hooks/useFilters'
import { AuthContext } from 'store'
import type { IActionFilter } from 'interfaces/filters.interface'
import { useFetchCategories } from './queries/useFetchCategories'
import { DatePickerRange } from 'shared/components/DatePickerRange/DatePickerRange'
import { useSearchParams } from 'libs/router'
import { format } from 'libs/dates'
import { parseDate } from 'shared/components/DatePickerRange/utils/utils'

const FILTER_PARAMS = [
	'product__category',
	'booking_id',
	'created_after',
	'created_before',
	'account_id'
]

type FiltersSectionProps = {
	checkedSales: number[]
	handleSearch: (data: ChangeEvent<HTMLInputElement>) => void
	handleFilter: ({ id, value }: { id: string; value: string | null }) => void
	commissionAction: () => void
	payoutAction: () => void
	exportAction: () => void
}

export const FiltersSection = ({
	checkedSales,
	handleSearch,
	handleFilter,
	commissionAction,
	payoutAction,
	exportAction
}: FiltersSectionProps) => {
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const { auth } = useContext(AuthContext)
	const { isUser } = useUserRoles()
	const [actionsFilter] = useFilters(checkedSales)
	const [isOpen, setIsOpen] = useState(FILTER_PARAMS.some((param) => searchParams.has(param)))
	const [resetKey, setResetKey] = useState(0)
	const [dateRange, setDateRange] = useState<[Date | undefined, Date | undefined]>([
		parseDate(searchParams.get('created_after')),
		parseDate(searchParams.get('created_before'))
	])
	const [start, end] = dateRange

	const { categories, isCategoriesLoading } = useFetchCategories()
	const categoryOptions = (categories?.data?.results ?? []).map((option: any) => ({
		label: option.name,
		value: option.id
	}))

	const {
		data: bookings,
		isLoading: loadingBookings,
		fetchNextPage: fetchNextBookings
	} = useFetchInfinite({
		name: 'fetchBookings',
		endpoint: '/bookings/',
		query: 'is_active=true',
		enabled: !!auth.accessToken
	})
	const bookingOptions = (bookings?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((booking: any) => ({
			label: booking?.booth?.number
				? `#${booking.booth.number} ${booking.booth.booth_type.name} - (#${booking.ref_nr} ${booking.start_date})`
				: `#${booking.ref_nr} ${booking.start_date}`,
			value: booking.id
		}))

	const handleActionFilter = (data: IActionFilter) => {
		switch (data.value) {
			case 'commission':
				commissionAction()
				break
			case 'payout':
				payoutAction()
				break
			case 'export':
				exportAction()
				break
		}
	}

	const handleDateFilter = (date: [Date | null, Date | null]) => {
		setDateRange([date[0] ?? undefined, date[1] ?? undefined])
		handleFilter({ id: 'created_after', value: date[0] ? format(date[0], 'yyyy-MM-dd') : null })
		handleFilter({ id: 'created_before', value: date[1] ? format(date[1], 'yyyy-MM-dd') : null })
	}

	const clearFilters = () => {
		const params = new URLSearchParams(searchParams)
		FILTER_PARAMS.forEach((param) => params.delete(param))
		params.delete('cursor')
		setSearchParams(params)
		setDateRange([undefined, undefined])
		setResetKey((key) => key + 1)
	}

	return (
		<div className="border-b border-border p-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				<SearchInput
					variant="filled"
					className="min-w-0 flex-1"
					label={t('Product search')}
					placeholder={t('Search sales...')}
					onChange={handleSearch}
				/>
				<FiltersToggle isOpen={isOpen} onClick={() => setIsOpen((open) => !open)} />
			</div>
			{isOpen && (
				<FiltersPanel key={resetKey} title={t('Filter sales')} onClear={clearFilters}>
					<FilterField label={t('Category')}>
						<Select
							id="category"
							variant="outline"
							placeholder={t('All')}
							isLoading={isCategoriesLoading}
							options={categoryOptions}
							onChange={(data) =>
								handleFilter({ id: 'product__category', value: data?.value || null })
							}
						/>
					</FilterField>
					<FilterField label={t('Booking')}>
						<Select
							id="booking"
							variant="outline"
							placeholder={t('All')}
							isLoading={loadingBookings}
							options={bookingOptions}
							handleInfiniteScroll={fetchNextBookings}
							onChange={(data: any) =>
								handleFilter({ id: 'booking_id', value: data?.value || null })
							}
						/>
					</FilterField>
					<FilterField label={t('Date')}>
						<DatePickerRange
							label=""
							variant="outline"
							start={start}
							end={end}
							onChange={handleDateFilter}
						/>
					</FilterField>
					{!isUser && (
						<FilterField label={t('User')}>
							<UserSelect
								id="user"
								variant="outline"
								placeholder={t('All')}
								onChange={(data: any) =>
									handleFilter({ id: 'account_id', value: data?.value || null })
								}
							/>
						</FilterField>
					)}
					{!isUser && (
						<FilterField label={actionsFilter.label}>
							<Select
								id={actionsFilter.id}
								variant="outline"
								placeholder={actionsFilter.placeholder}
								options={actionsFilter.options}
								onChange={(data) => data && handleActionFilter({ id: actionsFilter.id, ...data })}
							/>
						</FilterField>
					)}
				</FiltersPanel>
			)}
		</div>
	)
}
